import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Anchor, Factory } from "lucide-react";
import { ABOUT } from "../constants/testIds";
import { COMPANY, STATS, BRANDS } from "../lib/site-data";

const TIMELINE = [
  {
    year: "1998",
    title: "Tecnografic founded",
    body: "A new specialist in marine instrument & switch panels opens in Fort Lauderdale.",
  },
  {
    year: "2005",
    title: "OEM partnerships expand",
    body: "Selected as supplier by leading US boat builders for production dash assemblies.",
  },
  {
    year: "2012",
    title: "Hydrographic Dipping",
    body: "Becomes sole North American licensee of Taica's CUBIC printing technology in the marine sector.",
  },
  {
    year: "2018",
    title: "13,000 sq ft facility",
    body: "Expanded into a fully equipped 13,000 sq ft manufacturing facility with paint, print and engraving in-house.",
  },
  {
    year: "Today",
    title: "Trusted by global builders",
    body: "Serving the largest boat builders in the US and overseas with custom marine components — built to last.",
  },
];

export default function About() {
  return (
    <div data-testid={ABOUT.root}>
      <section className="bg-[color:var(--brand-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-20" />
        <div className="container-tg relative py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline text-[color:var(--brand-yellow)] mb-6">About</div>
              <h1 className="font-display text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none">
                25 years engineering <br />
                the marine <span className="text-[color:var(--brand-yellow)]">helm</span>.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-white/80 text-lg leading-relaxed">
                {COMPANY.shortAbout}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section data-testid={ABOUT.stats} className="bg-white border-b border-[color:var(--surface-border)]">
        <div className="container-tg py-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--surface-border)]">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white p-8">
              <div className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tight">
                {s.value}
              </div>
              <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-tg py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="overline mb-4">Our story</div>
          <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter">
            Marine craft meets industrial precision.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-6 text-slate-600 text-lg leading-relaxed">
          <p>
            Tecnografic was founded in 1998 on a simple belief: a boat's helm
            should feel as engineered as the hull beneath it. Over the past 25
            years that philosophy has shaped every dash, switch panel and trim
            piece we build.
          </p>
          <p>
            Today our team operates a 13,000 sq ft facility in Fort Lauderdale
            equipped with the latest CNC, laser, engraving, paint and
            hydrographic dipping equipment — backed by 3D-CAD design and
            decades of marine fabrication experience.
          </p>
          <p>
            Every product is made to order, designed for the harshest marine
            environment, and backed by a 5-year minimum warranty against fade
            or delamination.
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-[color:var(--surface-border)]">
        <div className="container-tg py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--surface-border)] border border-[color:var(--surface-border)]">
            {[
              { icon: Anchor, title: "Marine-First", body: "Every part engineered for salt, sun and spray." },
              { icon: ShieldCheck, title: "Warrantied", body: "5-year minimum against fade or delamination." },
              { icon: Factory, title: "In-House", body: "Design, engineering & finishing under one roof." },
              { icon: Award, title: "Trusted", body: "OEM supplier to the largest boat builders worldwide." },
            ].map((v) => (
              <div key={v.title} className="bg-white p-8">
                <div className="w-12 h-12 mb-5 bg-[color:var(--brand-yellow)] flex items-center justify-center">
                  <v.icon className="w-5 h-5 text-[color:var(--brand-navy)]" />
                </div>
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">{v.title}</h3>
                <p className="text-sm text-slate-600 mt-2">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid={ABOUT.timeline} className="container-tg py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <div className="overline mb-4">Milestones</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter">
              A quarter-century on the water.
            </h2>
          </div>
          <ol className="lg:col-span-8 relative border-l-2 border-[color:var(--brand-yellow)] pl-8 space-y-12">
            {TIMELINE.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[42px] top-1 w-5 h-5 bg-[color:var(--brand-yellow)] border-4 border-[color:var(--surface-bg)]" />
                <div className="overline text-[color:var(--brand-navy)] mb-2">{t.year}</div>
                <h3 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">{t.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed max-w-xl">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white border-y border-[color:var(--surface-border)]">
        <div className="container-tg py-20">
          <div className="overline mb-3 text-center">Partners</div>
          <h3 className="font-display text-3xl lg:text-4xl font-bold text-[color:var(--brand-navy)] text-center mb-12">
            Brands that trust Tecnografic.
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 items-center">
            {BRANDS.slice(0, 15).map((b) => (
              <div key={b.name} className="h-16 flex items-center justify-center grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all">
                <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--brand-navy)] text-white">
        <div className="container-tg py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tighter">
              Let's discuss your next build.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" className="btn-yellow w-full justify-center">
              Start a Project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
